import { aiModelCapabilities, platformConnectionCatalog, type AiProvider, type CredentialMetadata, type PlatformApplicationConfigMetadata } from "@control-plane/contracts";
import { ApiClientError, api } from "./api";

export type ServiceConfigSection = "platform" | "ai" | "sms";
export type ServiceCardStatus = "configured" | "verified" | "not_configured" | "failed";
export type ServiceCheckStatus = "verified" | "unverified" | "failed";
export type AiModelCapability = typeof aiModelCapabilities[number];

export type ServiceEvidence = {
  label: string;
  value: string;
  status?: ServiceCheckStatus | undefined;
};

export type ServiceSubItem = {
  key: string;
  title: string;
  description: string;
  status?: ServiceCardStatus | undefined;
  logoPath?: string | undefined;
  secretSummary: string;
  callbackLabel?: string | undefined;
  callbackUrl?: string | undefined;
  callbackStatus?: ServiceCheckStatus | undefined;
  callbackDetail?: string | undefined;
  connectionStatus?: "healthy" | "degraded" | "pending" | "revoked" | "missing" | undefined;
  connectionLastSuccessAt?: string | null | undefined;
  connectionLastError?: string | null | undefined;
  messageUrl?: string | undefined;
  messageStatus?: ServiceCheckStatus | undefined;
  messageDetail?: string | undefined;
  updatedAt: string | null;
};

export type AiCatalogModel = {
  key: string;
  name: string;
  priceLabel: string;
  purpose: string;
  budgetLabel?: string | undefined;
  fallbackLabel?: string | undefined;
  status?: "active" | "disabled" | "retired" | undefined;
  capabilities?: AiModelCapability[] | undefined;
  contextWindow?: number | null | undefined;
  maxOutputTokens?: number | null | undefined;
  currency?: "CNY" | "USD" | undefined;
  inputTokenPriceMicros?: number | undefined;
  outputTokenPriceMicros?: number | undefined;
  dailyTokenBudget?: number | null | undefined;
  dailyCostBudgetMinor?: number | null | undefined;
  monthlyCostBudgetMinor?: number | null | undefined;
  fallbackProvider?: string | null | undefined;
  fallbackModel?: string | null | undefined;
};

export type AiCatalogService = {
  key: string;
  title: string;
  models: AiCatalogModel[];
};

export type ServiceConfigCard = {
  section: ServiceConfigSection;
  title: string;
  summary: string;
  status: ServiceCardStatus;
  primaryAction: string;
  unconfiguredHint: string;
  evidence: ServiceEvidence[];
  items: ServiceSubItem[];
  catalogServices?: AiCatalogService[] | undefined;
  serviceUnavailable?: string | null | undefined;
};

export type ServiceConfigCenter = {
  cards: ServiceConfigCard[];
  loadedFromUnifiedEndpoint: boolean;
};

type AssistantDefault = {
  provider: AiProvider;
  model: string;
};

type MetadataResponse<T> = { items: T[] };

const aiProviderLabels: Record<string, string> = {
  qwen: "通义千问",
  deepseek: "DeepSeek",
  kimi: "Kimi",
  zhipu: "智谱 GLM",
  mimo: "MiMo",
  custom_openai_compatible: "自定义兼容模型",
  "ai-runtime": "AI 默认路线",
};

const credentialNameLabels: Record<string, string> = {
  "ai-provider-runtime": "模型账号密钥",
  "ai-agent-routing": "AI 默认路线",
  "sms-runtime": "短信发送密钥",
  "web-wechat-runtime": "微信扫码登录密钥",
  "platform-application": "平台连接密钥",
};

const credentialServiceLabels: Record<string, string> = {
  sms: "验证码短信",
  "web-wechat-login": "微信扫码登录",
};

function formatDateTime(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function secretSummary(saved: boolean, updatedAt: string | null, savedParts?: number) {
  if (!saved) return "必填信息未保存";
  const parts = savedParts && savedParts > 0 ? `已保存 ${savedParts} 项` : "已保存";
  return updatedAt ? `${parts} · ${updatedAt}` : parts;
}

function serviceSummaryFromStatus(status: ServiceCardStatus, updatedAt: string | null) {
  if (status === "verified") return updatedAt ? `服务端已确认可用 · ${updatedAt}` : "服务端已确认可用";
  if (status === "configured") return updatedAt ? `配置已保存 · ${updatedAt}` : "配置已保存";
  if (status === "failed") return updatedAt ? `服务端校验阻断 · ${updatedAt}` : "服务端校验阻断";
  return "必填信息未保存";
}

function systemVerified(status: unknown, source: unknown): ServiceCheckStatus {
  if (status === "failed") return "failed";
  if (status === "verified" && source === "system") return "verified";
  return "unverified";
}

function visibleCredentialName(item: CredentialMetadata) {
  return credentialNameLabels[item.name] ?? credentialServiceLabels[item.provider] ?? aiProviderLabels[item.provider] ?? "服务密钥";
}

function visibleCredentialProvider(item: CredentialMetadata) {
  return credentialServiceLabels[item.provider] ?? aiProviderLabels[item.provider] ?? "服务待确认";
}

async function optionalApi<T>(path: string): Promise<T | null> {
  try {
    return await api<T>(path);
  } catch (error) {
    if (error instanceof ApiClientError && [404, 501].includes(error.status)) return null;
    throw error;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function textOf(value: unknown, fallback = "") {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function numberOf(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function cardStatusOf(value: unknown): ServiceCardStatus {
  if (value === "verified") return "verified";
  if (value === "configured") return "configured";
  if (value === "blocked" || value === "degraded" || value === "failed") return "failed";
  return "not_configured";
}

function combinedServiceStatus(statuses: ServiceCardStatus[]): ServiceCardStatus {
  if (statuses.includes("failed")) return "failed";
  if (statuses.length && statuses.every((status) => status === "verified")) return "verified";
  if (statuses.some((status) => status === "verified" || status === "configured")) return "configured";
  return "not_configured";
}

function checkStatusOf(value: unknown, source?: unknown): ServiceCheckStatus {
  if (value === "failed") return "failed";
  if (source === "manual") return "unverified";
  if (value === "success" || value === "verified") return "verified";
  return "unverified";
}

function aiProviderTitle(provider: string) {
  return aiProviderLabels[provider] ?? "模型服务";
}

function priceLabel(currency: unknown, inputMicros: unknown, outputMicros: unknown) {
  const unit = currency === "USD" ? "$" : "¥";
  const input = numberOf(inputMicros);
  const output = numberOf(outputMicros);
  if (input === null || output === null) return "价格待服务端确认";
  const format = new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 6 });
  return `输入约 ${unit}${format.format(input / 1000)} / 千处理单位，输出约 ${unit}${format.format(output / 1000)} / 千处理单位`;
}

function budgetLabel(model: Record<string, unknown>) {
  const dailyTokens = numberOf(model.dailyTokenBudget);
  const dailyCost = numberOf(model.dailyCostBudgetMinor);
  const monthlyCost = numberOf(model.monthlyCostBudgetMinor);
  const parts = [
    dailyTokens ? `日 token ${dailyTokens}` : null,
    dailyCost ? `日预算 ${(dailyCost / 100).toFixed(2)}` : null,
    monthlyCost ? `月预算 ${(monthlyCost / 100).toFixed(2)}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join("，") : "预算待服务端确认";
}

function fallbackLabel(model: Record<string, unknown>) {
  const provider = textOf(model.fallbackProvider);
  const fallbackModel = textOf(model.fallbackModel);
  return provider && fallbackModel ? `${aiProviderTitle(provider)} · ${fallbackModel}` : "降级策略待服务端确认";
}

function optionalText(value: unknown) {
  const text = textOf(value);
  return text || undefined;
}

function optionalNumber(value: unknown) {
  const number = numberOf(value);
  return number === null ? undefined : number;
}

function optionalNullableNumber(value: unknown) {
  if (value === null) return null;
  return optionalNumber(value);
}

function capabilitiesOf(value: unknown): AiModelCapability[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((item): item is AiModelCapability => typeof item === "string" && (aiModelCapabilities as readonly string[]).includes(item));
}

function businessPriceLabel(value: unknown) {
  const text = textOf(value);
  const match = text.match(/^(CNY|USD)\s+input[=:]\s*([0-9.]+)\s+output[=:]\s*([0-9.]+)/i);
  if (!match) return text || "价格待服务端确认";
  const symbol = match[1]?.toUpperCase() === "USD" ? "$" : "¥";
  return `输入约 ${symbol}${match[2]} / 千处理单位，输出约 ${symbol}${match[3]} / 千处理单位`;
}

function normalizeUnifiedCard(value: unknown): ServiceConfigCard | null {
  if (!isRecord(value)) return null;
  const section = value.section;
  if (section !== "platform" && section !== "ai" && section !== "sms") return null;
  const rawItems = Array.isArray(value.items) ? value.items : [];
  const rawEvidence = Array.isArray(value.evidence) ? value.evidence : [];
  return {
    section,
    title: textOf(value.title, section === "platform" ? "电商平台应用" : section === "ai" ? "AI 模型服务" : "登录与短信"),
    summary: textOf(value.summary, "等待服务端返回配置状态"),
    status: cardStatusOf(value.status),
    primaryAction: textOf(value.primaryAction, "配置向导"),
    unconfiguredHint: textOf(value.unconfiguredHint, "当前还没有完整配置，不能显示为可用。"),
    serviceUnavailable: typeof value.serviceUnavailable === "string" ? value.serviceUnavailable : null,
    evidence: rawEvidence.map((item): ServiceEvidence | null => {
      if (!isRecord(item)) return null;
      return {
        label: textOf(item.label, "检查项"),
        value: textOf(item.value, "待确认"),
        status: item.status === "verified" || item.status === "failed" ? item.status : item.status === "unverified" ? "unverified" : undefined,
      };
    }).filter((item): item is ServiceEvidence => Boolean(item)),
    items: rawItems.map((item): ServiceSubItem | null => {
      if (!isRecord(item)) return null;
      const callback = isRecord(item.callback) ? item.callback : {};
      const status = cardStatusOf(item.status);
      return {
        key: textOf(item.key, crypto.randomUUID()),
        title: textOf(item.title, "服务待确认"),
        description: textOf(item.description, ""),
        status,
        logoPath: typeof item.logoPath === "string" ? item.logoPath : undefined,
        secretSummary: textOf(item.secretSummary, "必填信息未保存"),
        callbackLabel: textOf(callback.label),
        callbackUrl: textOf(callback.url),
        callbackStatus: systemVerified(callback.status, callback.source),
        callbackDetail: textOf(callback.detail, "等待系统校验"),
        messageUrl: optionalText(item.messageUrl),
        messageStatus: checkStatusOf(item.messageStatus, item.messageSource),
        messageDetail: optionalText(item.messageDetail),
        updatedAt: textOf(item.updatedAt) || null,
      };
    }).filter((item): item is ServiceSubItem => Boolean(item)),
    catalogServices: Array.isArray(value.catalogServices) ? normalizeCatalogServices(value.catalogServices) : undefined,
  };
}

function normalizeCatalogServices(values: unknown[]): AiCatalogService[] {
  return values.map((value): AiCatalogService | null => {
    if (!isRecord(value)) return null;
    const models = Array.isArray(value.models) ? value.models : [];
    return {
      key: textOf(value.key, crypto.randomUUID()),
      title: textOf(value.title, "模型服务"),
      models: models.map((model): AiCatalogModel | null => {
        if (!isRecord(model)) return null;
        return {
          key: textOf(model.key, crypto.randomUUID()),
          name: textOf(model.name, "模型待确认"),
          priceLabel: businessPriceLabel(model.priceLabel),
          purpose: textOf(model.purpose, "适用场景待确认"),
          budgetLabel: optionalText(model.budgetLabel),
          fallbackLabel: optionalText(model.fallbackLabel),
          status: model.status === "disabled" || model.status === "retired" ? model.status : model.status === "active" ? "active" : undefined,
          capabilities: capabilitiesOf(model.capabilities),
          contextWindow: optionalNullableNumber(model.contextWindow),
          maxOutputTokens: optionalNullableNumber(model.maxOutputTokens),
          currency: model.currency === "USD" ? "USD" : model.currency === "CNY" ? "CNY" : undefined,
          inputTokenPriceMicros: optionalNumber(model.inputTokenPriceMicros),
          outputTokenPriceMicros: optionalNumber(model.outputTokenPriceMicros),
          dailyTokenBudget: model.dailyTokenBudget === null ? null : optionalNumber(model.dailyTokenBudget),
          dailyCostBudgetMinor: model.dailyCostBudgetMinor === null ? null : optionalNumber(model.dailyCostBudgetMinor),
          monthlyCostBudgetMinor: model.monthlyCostBudgetMinor === null ? null : optionalNumber(model.monthlyCostBudgetMinor),
          fallbackProvider: model.fallbackProvider === null ? null : optionalText(model.fallbackProvider),
          fallbackModel: model.fallbackModel === null ? null : optionalText(model.fallbackModel),
        };
      }).filter((model): model is AiCatalogModel => Boolean(model)),
    };
  }).filter((item): item is AiCatalogService => Boolean(item));
}

function normalizeUnifiedCenter(value: unknown): ServiceConfigCenter | null {
  if (!isRecord(value)) return null;
  const cards = Array.isArray(value.cards) ? value.cards.map(normalizeUnifiedCard).filter((card): card is ServiceConfigCard => Boolean(card)) : [];
  if (!cards.length) return null;
  return { cards, loadedFromUnifiedEndpoint: true };
}

function normalizeServiceConfigCenterView(value: unknown): ServiceConfigCenter | null {
  if (!isRecord(value)) return null;
  const platformApplications = Array.isArray(value.platformApplications) ? value.platformApplications.filter(isRecord) : [];
  const ai = Array.isArray(value.ai) ? value.ai.filter(isRecord) : [];
  const sms = isRecord(value.sms) ? value.sms : null;
  if (!platformApplications.length && !ai.length && !sms) return null;
  return {
    loadedFromUnifiedEndpoint: true,
    cards: [
      normalizePlatformApplicationsView(platformApplications),
      normalizeAiView(ai),
      normalizeSmsView(sms),
    ],
  };
}

function normalizePlatformApplicationsView(items: Record<string, unknown>[]): ServiceConfigCard {
  const rows = items.map((item): ServiceSubItem => {
    const provider = textOf(item.provider);
    const catalog = platformConnectionCatalog.find((entry) => entry.provider === provider);
    const callbacks = Array.isArray(item.callbacks) ? item.callbacks.filter(isRecord) : [];
    const callback = callbacks.find((entry) => /oauth|auth|授权/i.test(textOf(entry.subject) || textOf(entry.kind) || textOf(entry.label))) ?? callbacks[0];
    const messageCallback = callbacks.find((entry) => /message|消息/i.test(textOf(entry.subject) || textOf(entry.kind) || textOf(entry.label)));
    const credential = isRecord(item.credential) ? item.credential : {};
    const connection = isRecord(item.connection) ? item.connection : {};
    const itemStatus = cardStatusOf(item.status);
    const topLevelUpdatedAt = formatDateTime(textOf(item.updatedAt));
    const hasCredentialDetail = isRecord(item.credential);
    const configured = credential.configured === true || (!hasCredentialDetail && (itemStatus === "configured" || itemStatus === "verified"));
    const lastUsedAt = textOf(credential.lastUsedAt);
    const updatedAt = formatDateTime(lastUsedAt) ?? topLevelUpdatedAt;
    return {
      key: provider || textOf(item.label, "platform"),
      title: textOf(item.label, catalog?.label ?? "平台应用"),
      description: catalog?.authorizationGuide ?? "按官方后台要求保存应用资料并等待系统验证。",
      status: itemStatus,
      logoPath: catalog?.logoPath,
      secretSummary: hasCredentialDetail ? (configured ? `已加密保存 · 最近使用 ${formatDateTime(lastUsedAt) ?? "暂无"}` : "必填信息未保存") : serviceSummaryFromStatus(itemStatus, updatedAt),
      callbackLabel: "回调验证",
      callbackUrl: textOf(callback?.url),
      callbackStatus: checkStatusOf(callback?.status, callback?.source),
      callbackDetail: callback?.status === "success" && callback?.source !== "manual" ? "系统已收到真实回调验证" : "等待系统收到真实回调后校验",
      connectionStatus: connection.status === "healthy" || connection.status === "degraded" || connection.status === "pending" || connection.status === "revoked" ? connection.status : "missing",
      connectionLastSuccessAt: textOf(connection.lastSuccessAt) || null,
      connectionLastError: textOf(connection.lastError) || null,
      messageUrl: optionalText(messageCallback?.url),
      messageStatus: messageCallback ? checkStatusOf(messageCallback.status, messageCallback.source) : "unverified",
      messageDetail: messageCallback?.status === "success" && messageCallback.source !== "manual" ? "系统已收到真实消息回调验证" : "等待系统收到真实消息回调后校验",
      updatedAt,
    };
  });
  const configured = rows.filter((item) => item.status === "configured" || item.status === "verified" || item.secretSummary.startsWith("已加密保存")).length;
  const statuses = rows.map((item) => item.status ?? "not_configured");
  const overallStatus = combinedServiceStatus(statuses);
  return {
    section: "platform",
    title: "电商平台应用",
    summary: configured ? `${configured}/${rows.length} 个平台已保存应用资料` : "还没有平台应用完成必填资料",
    status: overallStatus,
    primaryAction: "配置平台应用",
    unconfiguredHint: "没有保存应用资料的平台不能让商家放心授权。",
    evidence: [
      { label: "密钥状态", value: configured ? "已有加密保存记录" : "缺少必填资料", status: configured ? "unverified" : "failed" },
      { label: "回调状态", value: "仅系统收到真实回调后才会显示已验证", status: rows.some((item) => item.callbackStatus === "verified") ? "verified" : "unverified" },
      { label: "连接测试", value: "店铺授权后到平台连接页执行真实探测", status: "unverified" },
    ],
    items: rows,
  };
}

function normalizeAiView(items: Record<string, unknown>[]): ServiceConfigCard {
  const catalogServices: AiCatalogService[] = items.map((item): AiCatalogService => {
    const provider = textOf(item.provider);
    const models = Array.isArray(item.models) ? item.models.filter(isRecord) : [];
    return {
      key: provider,
      title: aiProviderTitle(provider),
      models: models.map((model): AiCatalogModel => ({
        key: textOf(model.model, textOf(model.displayName, "model")),
        name: textOf(model.displayName, textOf(model.model, "模型待确认")),
        priceLabel: priceLabel(model.currency, model.inputTokenPriceMicros, model.outputTokenPriceMicros),
        purpose: model.status === "active" ? "可用于正式服务" : "当前不可用于正式服务",
        budgetLabel: budgetLabel(model),
        fallbackLabel: fallbackLabel(model),
        status: model.status === "disabled" || model.status === "retired" ? model.status : "active",
        capabilities: capabilitiesOf(model.capabilities),
        contextWindow: optionalNullableNumber(model.contextWindow),
        maxOutputTokens: optionalNullableNumber(model.maxOutputTokens),
        currency: model.currency === "USD" ? "USD" : "CNY",
        inputTokenPriceMicros: optionalNumber(model.inputTokenPriceMicros),
        outputTokenPriceMicros: optionalNumber(model.outputTokenPriceMicros),
        dailyTokenBudget: model.dailyTokenBudget === null ? null : optionalNumber(model.dailyTokenBudget),
        dailyCostBudgetMinor: model.dailyCostBudgetMinor === null ? null : optionalNumber(model.dailyCostBudgetMinor),
        monthlyCostBudgetMinor: model.monthlyCostBudgetMinor === null ? null : optionalNumber(model.monthlyCostBudgetMinor),
        fallbackProvider: model.fallbackProvider === null ? null : optionalText(model.fallbackProvider),
        fallbackModel: model.fallbackModel === null ? null : optionalText(model.fallbackModel),
      })),
    };
  }).filter((service) => service.key);
  const rows = items.map((item): ServiceSubItem => {
    const provider = textOf(item.provider);
    const credential = isRecord(item.credential) ? item.credential : {};
    const itemStatus = cardStatusOf(item.status);
    const hasCredentialDetail = isRecord(item.credential);
    const configured = credential.configured === true || (!hasCredentialDetail && (itemStatus === "configured" || itemStatus === "verified"));
    const lastTest = isRecord(item.lastTest) ? item.lastTest : null;
    const topLevelUpdatedAt = formatDateTime(textOf(item.updatedAt));
    const updatedAt = lastTest ? formatDateTime(textOf(lastTest.checkedAt)) : formatDateTime(textOf(credential.lastUsedAt)) ?? topLevelUpdatedAt;
    return {
      key: provider,
      title: aiProviderTitle(provider),
      description: "模型账号与连通测试",
      status: itemStatus,
      secretSummary: hasCredentialDetail ? (configured ? `已加密保存 · 最近使用 ${formatDateTime(textOf(credential.lastUsedAt)) ?? "暂无"}` : "模型账号未保存") : serviceSummaryFromStatus(itemStatus, updatedAt),
      callbackStatus: lastTest ? checkStatusOf(lastTest.status, lastTest.source) : "unverified",
      callbackLabel: "连通测试",
      callbackDetail: lastTest?.status === "success" ? "最近一次真实连通测试通过" : "等待真实连通测试",
      updatedAt,
    };
  });
  const configuredCount = rows.filter((item) => item.secretSummary.startsWith("已加密保存")).length;
  const anyVerifiedTest = rows.some((item) => item.callbackStatus === "verified");
  const statuses = rows.map((item) => item.status ?? "not_configured");
  const overallStatus = statuses.length ? combinedServiceStatus(statuses) : catalogServices.length && configuredCount ? "configured" : catalogServices.length ? "not_configured" : "failed";
  return {
    section: "ai",
    title: "AI 模型服务",
    summary: catalogServices.length ? `${catalogServices.length} 个服务商目录已从服务端读取` : rows.length ? `${rows.length} 个模型服务已返回可用性` : "模型与价格目录未接入，不能新增或切换模型",
    status: overallStatus,
    primaryAction: "配置 AI 服务",
    unconfiguredHint: "AI 必须先由服务端返回模型、价格和预算口径，不能在页面写死模型。",
    serviceUnavailable: catalogServices.length || rows.length ? null : "服务端模型目录未接入，本页不会把硬编码模型包装成可用配置。",
    evidence: [
      { label: "连通测试", value: anyVerifiedTest ? "已有真实连通测试通过" : "等待真实模型接口测试结果", status: anyVerifiedTest ? "verified" : "unverified" },
      { label: "预算", value: catalogServices.some((service) => service.models.some((model) => model.budgetLabel && !model.budgetLabel.includes("待服务端确认"))) ? "已读取服务端预算口径" : "等待服务端预算策略", status: "unverified" },
      { label: "降级", value: catalogServices.some((service) => service.models.some((model) => model.fallbackLabel && !model.fallbackLabel.includes("待服务端确认"))) ? "已读取服务端降级策略" : "等待服务端降级策略", status: "unverified" },
      { label: "审计证据", value: configuredCount ? "已有配置审计摘要" : "暂无配置审计摘要", status: configuredCount ? "unverified" : "failed" },
    ],
    items: rows,
    catalogServices,
  };
}

function normalizeSmsView(value: Record<string, unknown> | null): ServiceConfigCard {
  const credential = value && isRecord(value.credential) ? value.credential : {};
  const smsStatus = cardStatusOf(value?.status);
  const hasCredentialDetail = Boolean(value && isRecord(value.credential));
  const configured = credential.configured === true || (!hasCredentialDetail && (smsStatus === "configured" || smsStatus === "verified"));
  const lastSendTest = value && isRecord(value.lastSendTest) ? value.lastSendTest : null;
  const callback = value && isRecord(value.callback) ? value.callback : {};
  const callbackStatus = checkStatusOf(callback.status, callback.source);
  const sendStatus = lastSendTest ? checkStatusOf(lastSendTest.status) : "unverified";
  const balanceStatus = lastSendTest?.balanceStatus === "success" ? "verified" : lastSendTest?.status === "failed" ? "failed" : "unverified";
  const webWechatSource = value ? (isRecord(value.webWechatLogin) ? value.webWechatLogin : isRecord(value.wechatLogin) ? value.wechatLogin : {}) : {};
  const webWechat = isRecord(webWechatSource) ? webWechatSource : {};
  const webWechatCredential = isRecord(webWechat.credential) ? webWechat.credential : {};
  const webWechatCallback = isRecord(webWechat.callback) ? webWechat.callback : {};
  const webWechatStatus = cardStatusOf(webWechat.status);
  const hasWebWechatCredentialDetail = isRecord(webWechat.credential);
  const webWechatConfigured = webWechatCredential.configured === true || (!hasWebWechatCredentialDetail && (webWechatStatus === "configured" || webWechatStatus === "verified"));
  const webWechatCallbackStatus = checkStatusOf(webWechatCallback.status, webWechatCallback.source);
  const smsUpdatedAt = lastSendTest ? formatDateTime(textOf(lastSendTest.checkedAt)) : formatDateTime(textOf(credential.lastUsedAt)) ?? formatDateTime(textOf(value?.updatedAt));
  const webWechatUpdatedAt = formatDateTime(textOf(webWechatCredential.lastUsedAt)) ?? formatDateTime(textOf(webWechat.updatedAt));
  const overallStatus = combinedServiceStatus([smsStatus, webWechatStatus]);
  return {
    section: "sms",
    title: "登录与短信",
    summary: configured || webWechatConfigured ? "验证码短信与微信登录分别按服务端状态展示" : "短信签名、模板和发送账号尚未配置",
    status: overallStatus,
    primaryAction: "配置登录与短信",
    unconfiguredHint: "没有短信签名、模板和真实发送测试前，不显示为可用；送达回执不作为发送前置条件。",
    evidence: [
      { label: "签名与模板", value: configured ? "已加密保存，页面不显示原文" : "尚未保存", status: configured ? "unverified" : "failed" },
      { label: "测试手机号", value: "由配置表单填写，只用于真实发送测试", status: "unverified" },
      { label: "真实发送测试", value: lastSendTest?.status === "success" ? "最近一次真实发送测试通过" : "等待服务端发送回执", status: sendStatus },
      { label: "余额", value: textOf(lastSendTest?.balanceStatus, "等待服务端余额回读；失败会显示失败原因"), status: balanceStatus },
      { label: "送达回执（可选）", value: callbackStatus === "verified" ? "已收到服务商送达回执" : "服务商返回后由系统自动记录，不阻塞短信启用", status: callbackStatus },
    ],
    items: [
      {
        key: "sms-runtime",
        title: "验证码短信",
        description: "登录验证码、注册校验和密码重置",
        status: smsStatus,
        secretSummary: hasCredentialDetail ? (configured ? `已加密保存 · 最近使用 ${formatDateTime(textOf(credential.lastUsedAt)) ?? "暂无"}` : "必填信息未保存") : serviceSummaryFromStatus(smsStatus, smsUpdatedAt),
        callbackLabel: "状态回调",
        callbackUrl: textOf(callback.url),
        callbackStatus,
        callbackDetail: callbackStatus === "verified" ? "系统已收到真实回执验证" : "等待系统收到服务商回执后校验",
        updatedAt: smsUpdatedAt,
      },
      {
        key: "web-wechat-runtime",
        title: "微信扫码登录",
        description: "微信开放平台网站应用登录",
        status: webWechatStatus,
        secretSummary: hasWebWechatCredentialDetail ? (webWechatConfigured ? `已加密保存 · 最近使用 ${formatDateTime(textOf(webWechatCredential.lastUsedAt)) ?? "暂无"}` : "必填信息未保存") : serviceSummaryFromStatus(webWechatStatus, webWechatUpdatedAt),
        callbackLabel: "授权回调",
        callbackUrl: textOf(webWechatCallback.url),
        callbackStatus: webWechatCallbackStatus,
        callbackDetail: webWechatCallbackStatus === "verified" ? "系统已收到真实授权回调验证" : "等待系统收到微信授权回调后校验",
        updatedAt: webWechatUpdatedAt,
      },
    ],
  };
}

async function loadLegacyServiceConfigCenter(): Promise<ServiceConfigCenter> {
  const [platformConfigs, credentials, assistantDefault, aiCatalog] = await Promise.all([
    api<MetadataResponse<PlatformApplicationConfigMetadata>>("/api/platform-app-configs"),
    api<MetadataResponse<CredentialMetadata>>("/api/credentials?scope=platform"),
    optionalApi<AssistantDefault>("/api/assistant/default"),
    optionalApi<{ services?: unknown[] }>("/api/service-config/ai-catalog"),
  ]);
  return {
    loadedFromUnifiedEndpoint: false,
    cards: [
      buildPlatformCard(platformConfigs.items),
      buildAiCard(credentials.items, assistantDefault, aiCatalog?.services ? normalizeCatalogServices(aiCatalog.services) : null),
      buildSmsCard(credentials.items),
    ],
  };
}

export async function loadServiceConfigCenter({ allowLegacyFallback = true }: { allowLegacyFallback?: boolean } = {}): Promise<ServiceConfigCenter> {
  const unified = await optionalApi<unknown>("/api/service-config/center");
  const normalized = normalizeUnifiedCenter(unified);
  if (normalized) return normalized;
  const centerView = normalizeServiceConfigCenterView(unified);
  if (centerView) return centerView;
  if (!allowLegacyFallback) throw new ApiClientError(501, "SERVICE_CONFIG_CENTER_REQUIRED", "服务配置中心尚未接入，当前账号不能读取底层配置保险库。");
  return loadLegacyServiceConfigCenter();
}

function buildPlatformCard(configs: PlatformApplicationConfigMetadata[]): ServiceConfigCard {
  const configured = new Map(configs.map((item) => [item.provider, item]));
  const items = platformConnectionCatalog.map((item): ServiceSubItem => {
    const metadata = configured.get(item.provider);
    const updatedAt = formatDateTime(metadata?.updatedAt ?? null);
    return {
      key: item.provider,
      title: item.label,
      description: item.authorizationGuide,
      logoPath: item.logoPath,
      secretSummary: secretSummary(Boolean(metadata), updatedAt, metadata?.configuredFields.length),
      callbackLabel: "授权回调",
      callbackUrl: metadata?.callbackUrl ?? undefined,
      callbackStatus: "unverified",
      callbackDetail: metadata?.callbackUrl ? "等待系统收到真实授权回调后校验" : "服务端尚未返回完整回调地址，不能登记为可用",
      messageUrl: metadata?.messageUrl ?? undefined,
      messageStatus: "unverified",
      messageDetail: metadata?.messageUrl ? "等待系统收到真实消息回调后校验" : "服务端尚未返回完整消息推送地址",
      updatedAt,
    };
  });
  const readyCount = configs.filter((item) => item.status === "active").length;
  return {
    section: "platform",
    title: "电商平台应用",
    summary: readyCount ? `${readyCount}/${platformConnectionCatalog.length} 个平台已保存应用资料，回调仍需系统验证` : "还没有平台应用完成必填资料",
    status: readyCount ? "configured" : "not_configured",
    primaryAction: "配置平台应用",
    unconfiguredHint: "没有保存应用资料的平台不能让商家放心授权。",
    evidence: [
      { label: "密钥状态", value: readyCount ? "已有加密保存记录" : "缺少必填资料", status: readyCount ? "unverified" : "failed" },
      { label: "回调状态", value: "仅系统收到真实回调后才会显示已验证", status: "unverified" },
      { label: "连接测试", value: "店铺授权后到平台连接页执行真实探测", status: "unverified" },
    ],
    items,
  };
}

function buildAiCard(credentials: CredentialMetadata[], assistantDefault: AssistantDefault | null, catalogServices: AiCatalogService[] | null): ServiceConfigCard {
  const providerCredentials = credentials.filter((item) => item.name === "ai-provider-runtime" && item.status === "active");
  const defaultTitle = assistantDefault ? `${aiProviderLabels[assistantDefault.provider] ?? "模型服务"} · ${assistantDefault.model}` : "尚未设置默认模型";
  const hasCatalog = Boolean(catalogServices?.length);
  return {
    section: "ai",
    title: "AI 模型服务",
    summary: hasCatalog
      ? `${catalogServices!.length} 个服务商目录已从服务端读取`
      : "模型与价格目录未接入，不能新增或切换模型",
    status: hasCatalog && providerCredentials.length ? "configured" : hasCatalog ? "not_configured" : "failed",
    primaryAction: "配置 AI 服务",
    unconfiguredHint: "AI 必须先由服务端返回模型、价格和预算口径，不能在页面写死模型。",
    serviceUnavailable: hasCatalog ? null : "服务端模型目录未接入，本页不会把硬编码模型包装成可用配置。",
    evidence: [
      { label: "默认模型", value: defaultTitle, status: assistantDefault ? "unverified" : "failed" },
      { label: "连通测试", value: "等待真实模型接口测试结果", status: "unverified" },
      { label: "预算", value: "等待服务端预算策略", status: "unverified" },
      { label: "降级", value: "等待服务端降级策略", status: "unverified" },
      { label: "审计证据", value: providerCredentials.length ? `最近配置：${formatDateTime(providerCredentials[0]?.updatedAt) ?? "未记录"}` : "暂无配置审计摘要", status: providerCredentials.length ? "unverified" : "failed" },
    ],
    items: providerCredentials.map((item) => ({
      key: `${item.provider}-${item.name}`,
      title: aiProviderLabels[item.provider] ?? visibleCredentialProvider(item),
      description: visibleCredentialName(item),
      secretSummary: secretSummary(true, formatDateTime(item.updatedAt)),
      updatedAt: formatDateTime(item.updatedAt),
    })),
    catalogServices: catalogServices ?? undefined,
  };
}

function buildSmsCard(credentials: CredentialMetadata[]): ServiceConfigCard {
  const smsCredential = credentials.find((item) => item.provider === "sms" && item.name === "sms-runtime");
  const wechatCredential = credentials.find((item) => item.provider === "web-wechat-login" && item.name === "web-wechat-runtime");
  const updatedAt = formatDateTime(smsCredential?.updatedAt ?? null);
  const wechatUpdatedAt = formatDateTime(wechatCredential?.updatedAt ?? null);
  return {
    section: "sms",
    title: "登录与短信",
    summary: smsCredential ? "短信账号已加密保存，仍需真实发送测试和余额回读" : "短信签名、模板和发送账号尚未配置",
    status: smsCredential ? "configured" : "not_configured",
    primaryAction: "配置登录与短信",
    unconfiguredHint: "没有短信签名、模板和真实发送测试前，不显示为可用。",
    evidence: [
      { label: "签名与模板", value: smsCredential ? "已加密保存，页面不显示原文" : "尚未保存", status: smsCredential ? "unverified" : "failed" },
      { label: "测试手机号", value: "由配置向导填写，只用于真实发送测试", status: "unverified" },
      { label: "真实发送测试", value: "等待服务端发送回执", status: "unverified" },
      { label: "余额", value: "等待服务端余额回读；失败会显示失败原因", status: "unverified" },
      { label: "回调验证", value: "仅系统回调验证通过后才显示已验证", status: "unverified" },
    ],
    items: [
      {
        key: "sms-runtime",
        title: "验证码短信",
        description: "登录验证码、注册校验和密码重置",
        secretSummary: secretSummary(Boolean(smsCredential), updatedAt),
        callbackLabel: "状态回调",
        callbackStatus: "unverified",
        callbackDetail: "等待系统收到服务商回执后校验",
        updatedAt,
      },
      {
        key: "web-wechat-runtime",
        title: "微信扫码登录",
        description: "微信开放平台网站应用登录",
        secretSummary: secretSummary(Boolean(wechatCredential), wechatUpdatedAt),
        callbackLabel: "授权回调",
        callbackStatus: "unverified",
        callbackDetail: "等待服务端返回完整回调地址并完成校验",
        updatedAt: wechatUpdatedAt,
      },
    ],
  };
}
